import { useEffect, useRef, useState } from 'react';

/**
 * Computes a simple normalized mic level (0–1) from a MediaStream using the Web Audio API.
 */
export function useMicLevel(stream: MediaStream | null, enabled: boolean): number {
  const [level, setLevel] = useState(0);
  const animationFrameIdRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

  useEffect(() => {
    if (!enabled || !stream) {
      setLevel(0);
      return;
    }

    const AudioContextCtor = window?.AudioContext;

    if (!AudioContextCtor) {
      setLevel(0);
      return;
    }

    const audioContext = new AudioContextCtor();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();

    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;

    source.connect(analyser);

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;
    sourceRef.current = source;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const updateLevel = () => {
      if (!analyserRef.current) return;
      analyserRef.current.getByteTimeDomainData(dataArray);

      // Compute a simple RMS value and normalize it.
      let sumSquares = 0;
      for (let i = 0; i < dataArray.length; i += 1) {
        const value = (dataArray[i] - 128) / 128; // -1 to 1
        sumSquares += value * value;
      }
      const rms = Math.sqrt(sumSquares / dataArray.length); // 0–1

      // Map RMS to a slightly boosted range so quiet speech is still visible.
      const normalized = Math.min(1, rms * 3);
      setLevel(normalized);

      animationFrameIdRef.current = requestAnimationFrame(updateLevel);
    };

    animationFrameIdRef.current = requestAnimationFrame(updateLevel);

    return () => {
      if (animationFrameIdRef.current !== null) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
      animationFrameIdRef.current = null;
      setLevel(0);

      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect();
        analyserRef.current = null;
      }
      if (audioContextRef.current) {
        // Closing can throw if already closed; ignore errors.
        try {
          void audioContextRef.current.close();
        } catch {
          // ignore
        }
        audioContextRef.current = null;
      }
    };
  }, [enabled, stream]);

  return level;
}
